import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { DELETE_CMZ_LIBRARY_QUOTE } from "../../../graphql/IslamicQuotes/mutation";
import { IslamicQuote } from "../../../graphql/IslamicQuotes/types";

type Props = {
  quote: IslamicQuote | null; // null → closed
  onClose: (didDelete: boolean) => void;
};

const DeleteConfirmDialog: React.FC<Props> = ({ quote, onClose }) => {
  const [error, setError] = useState<string | null>(null);
  const [deleteQuote, { loading }] = useMutation(DELETE_CMZ_LIBRARY_QUOTE);

  const handleDelete = async () => {
    if (!quote) return;
    setError(null);
    try {
      await deleteQuote({ variables: { id: quote.id } });
      onClose(true);
    } catch (err: any) {
      setError(
        err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          "Failed to delete quote."
      );
    }
  };

  return (
    <Dialog
      open={!!quote}
      onClose={() => !loading && onClose(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle fontWeight={700}>Delete this quote?</DialogTitle>
      <DialogContent>
        <Stack gap={1.5}>
          {error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          <DialogContentText>
            This permanently removes the quote from the global library for all
            mosques. This action cannot be undone.
          </DialogContentText>
          {quote && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              {quote.reference}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => onClose(false)} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={loading}
          sx={{ borderRadius: 2 }}
        >
          {loading ? "Deleting…" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmDialog;
